import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Type } from "lucide-react";
import adminApi, { apiError } from "@/lib/adminApi";
import api from "@/lib/api";
import { CONTENT_GROUPS } from "@/lib/contentFields";

const inputClass =
  "bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-gold outline-none w-full";

const AdminContenus = () => {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content")
      .then(({ data }) => {
        const initial = {};
        CONTENT_GROUPS.forEach((g) =>
          g.fields.forEach((f) => {
            initial[f.key] = (data && typeof data[f.key] === "string" && data[f.key].trim()) ? data[f.key] : f.def;
          })
        );
        setValues(initial);
      })
      .catch(() => toast.error("Chargement des contenus impossible."))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/content", { values });
      toast.success("Textes enregistrés — visibles immédiatement sur le site.");
    } catch (err) {
      toast.error(apiError(err, "Enregistrement impossible."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={28} className="animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div data-testid="admin-contenus" className="space-y-6 max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display uppercase text-4xl tracking-wide">Contenus du site</h1>
          <p className="mt-2 text-sm text-white/50">
            Modifiez les titres et descriptions de chaque page. Les changements sont visibles immédiatement sur le site après enregistrement.
          </p>
        </div>
        <button
          data-testid="content-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-7 py-3.5 bg-rouge text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Enregistrer tout
        </button>
      </div>

      <div className="flex items-start gap-3 text-xs text-white/45 bg-white/[0.03] border border-white/10 p-4">
        <Type size={15} className="text-gold shrink-0 mt-0.5" />
        Pour les grands titres : une ligne de texte = une ligne affichée. Sur les pages avec grand titre, la 2ème ligne s'affiche en or.
      </div>

      {CONTENT_GROUPS.map((g) => (
        <section key={g.group} data-testid={`content-group-${g.group.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="bg-noir-surface border border-noir-border p-6 space-y-4">
          <h2 className="font-display uppercase text-xl tracking-wide text-gold">{g.group}</h2>
          <div className="space-y-4">
            {g.fields.map((f) => (
              <div key={f.key}>
                <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-white/50 mb-2">{f.label}</label>
                {f.multiline ? (
                  <textarea
                    data-testid={`content-field-${f.key}`}
                    rows={3}
                    value={values[f.key] ?? ""}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                    className={`${inputClass} resize-y`}
                  />
                ) : (
                  <input
                    data-testid={`content-field-${f.key}`}
                    value={values[f.key] ?? ""}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                    className={inputClass}
                  />
                )}
              </div>
            ))}
          </div>
        </section>
      ))}

      <div className="sticky bottom-5 flex justify-end">
        <button
          data-testid="content-save-bottom"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-7 py-3.5 bg-rouge text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover disabled:opacity-50 shadow-[0_0_24px_rgba(230,0,0,0.35)]"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Enregistrer tout
        </button>
      </div>
    </div>
  );
};

export default AdminContenus;
